/**
 * Settings Context
 *
 * Centralized settings management with age-appropriate defaults.
 * This context provides a single source of truth for all user settings,
 * automatically falling back to age-based statistics when user values are missing.
 *
 * ## Architecture
 *
 * Settings → Never Null/Undefined
 *
 * All getter properties return non-null values:
 * - If user has set a value: return that value
 * - Financial fields (income, expenses, assets, liabilities): unset → 0
 * - Assumption fields (retirementAge, rates, etc.): unset → age-appropriate statistic
 *
 * ## Usage
 *
 * ```typescript
 * const { settings, loading } = useSettings();
 *
 * // Always returns a number - never null/undefined
 * const monthlyIncome = settings.income.monthlyIncome;
 * const totalAssets = settings.assets.totalAssets;
 * const currentAge = settings.profile.currentAge;
 * ```
 *
 * ## Memoization
 *
 * Settings objects are memoized and only recalculated when:
 * - User settings change in Firestore
 * - Statistics cache is updated
 * - User's calculated age changes
 *
 * This ensures calculations using settings are not unnecessarily recomputed.
 *
 * ## Statistics Integration
 *
 * Statistics are loaded per-age from static JSON via loadStatisticsForAge()
 * and stored in local state. The statistics map is passed to each *SettingsWithDefaults constructor so
 * field values can fall back to age-appropriate statistics when user data is missing.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from "react";
import { useAuth } from "../hooks/useAuth";
import { subscribeToSettings } from "../services/settingsService";
import {
  loadStatisticsForAge,
  getFieldStatisticValue,
} from "../services/statisticsService";
import type { AgeStatistics } from "../types/statistics";
import { calculateAgeFromYear } from "../utils/ageCalculations";
import { createLogger } from "../utils/logger";
import type {
  ProfileSettingsData,
  InvestableAssetsSettingsData,
  RealEstateAssetsSettingsData,
  LiabilitiesSettingsData,
  IncomeSettingsData,
  ExpensesSettingsData,
  AssumptionsSettingsData,
} from "../types/settings";

const logger = createLogger({ prefix: "SettingsContext" });

/**
 * Default age used when the user's year of birth is not yet known.
 * Chosen as the approximate Canadian median first-time homebuyer age,
 * providing reasonable statistical defaults during the loading window
 * before the profile is fetched from Firestore.
 */
export const DEFAULT_AGE = 30;

/**
 * Base class shared by all settings classes.
 * Holds `data`, `age`, and `statisticsMap` and defines the serialization contract.
 *
 * Provides `getNumericFieldValue()` (returns `number`, throws on
 * non-numeric) for financial and assumption fields.
 */
abstract class SettingsBase<T> {
  protected data: T;
  protected age: number;
  protected statisticsMap: AgeStatistics;

  constructor(data: T, age: number, statisticsMap: AgeStatistics) {
    this.data = data;
    this.age = age;
    this.statisticsMap = statisticsMap;
  }

  /**
   * Get a numeric field value. Throws if the stored value is not a number.
   * Financial fields (income, expenses, assets, liabilities) default to 0
   * when the user hasn't provided a value. Assumption fields override this
   * to fall back to age-appropriate statistics instead.
   */
  protected getNumericFieldValue(fieldName: keyof T & string): number {
    const raw = this.data[fieldName];
    if (raw !== null && raw !== undefined) {
      if (typeof raw === "number") return raw;
      throw new Error(
        `Expected numeric value for field "${fieldName}", got ${typeof raw}`
      );
    }
    return 0;
  }

  abstract toJSON(): T;

  /**
   * Get raw data for serialization, preserving null values.
   * Used when saving scenarios to store user's actual values (or null for unset).
   */
  toRawJSON(): Partial<T> {
    return this.data;
  }
}

/**
 * Profile Settings with age-average defaults
 *
 * ProfileSettingsData contains mixed types (number | null, boolean | null)
 * which don't satisfy Record<string, number>. This class extends SettingsBase
 * directly to share the data/age constructor without requiring index-signature
 * conformance. Boolean fields like isRetired are handled directly.
 */
export class ProfileSettings extends SettingsBase<ProfileSettingsData> {
  /**
   * Get current age (calculated from yearOfBirth)
   * Returns the calculated age derived from yearOfBirth in profile data
   */
  get currentAge(): number {
    return this.age;
  }

  /**
   * Get year of birth
   * Returns actual value if set, otherwise calculates from current age
   */
  get yearOfBirth(): number {
    if (this.data.yearOfBirth !== null && this.data.yearOfBirth !== undefined) {
      return this.data.yearOfBirth;
    }
    const currentYear = new Date().getFullYear();
    return currentYear - this.age;
  }

  /**
   * Check if user is retired
   * Returns actual value if set, otherwise assumes false (working)
   */
  get isRetired(): boolean {
    return this.data.isRetired ?? false;
  }

  /**
   * Years lived in Canada after age 18 (used for OAS eligibility)
   * Returns actual value if set, otherwise null (unknown)
   */
  get yearsInCanadaAfterAge18(): number {
    return this.getNumericFieldValue("yearsInCanadaAfterAge18");
  }

  /**
   * Get raw data for serialization
   */
  toJSON(): ProfileSettingsData {
    return {
      yearOfBirth: this.yearOfBirth,
      isRetired: this.isRetired,
    };
  }
}

/**
 * Investable Assets Settings with age-average defaults
 *
 * Covers cash savings, registered accounts (RRSP, TFSA, FHSA, RESP, LIRA,
 * RRIF, DPSP), non-registered investments, and employer-sponsored plans.
 */
export class InvestableAssetsSettings extends SettingsBase<InvestableAssetsSettingsData> {
  get cashSavings(): number {
    return this.getNumericFieldValue("cashSavings");
  }
  get emergencyFund(): number {
    return this.getNumericFieldValue("emergencyFund");
  }
  get gicTermDeposits(): number {
    return this.getNumericFieldValue("gicTermDeposits");
  }
  get rrspBalance(): number {
    return this.getNumericFieldValue("rrspBalance");
  }
  get spousalRrspBalance(): number {
    return this.getNumericFieldValue("spousalRrspBalance");
  }
  get tfsaBalance(): number {
    return this.getNumericFieldValue("tfsaBalance");
  }
  get fhsaBalance(): number {
    return this.getNumericFieldValue("fhsaBalance");
  }
  get respBalance(): number {
    return this.getNumericFieldValue("respBalance");
  }
  get liraBalance(): number {
    return this.getNumericFieldValue("liraBalance");
  }
  get rrifBalance(): number {
    return this.getNumericFieldValue("rrifBalance");
  }
  get dpspBalance(): number {
    return this.getNumericFieldValue("dpspBalance");
  }
  get nonRegisteredInvestments(): number {
    return this.getNumericFieldValue("nonRegisteredInvestments");
  }
  get dbPensionCommutedValue(): number {
    return this.getNumericFieldValue("dbPensionCommutedValue");
  }
  get dcPensionBalance(): number {
    return this.getNumericFieldValue("dcPensionBalance");
  }
  get groupRrspBalance(): number {
    return this.getNumericFieldValue("groupRrspBalance");
  }
  get stockOptionsRsusValue(): number {
    return this.getNumericFieldValue("stockOptionsRsusValue");
  }

  get totalInvestableAssets(): number {
    return (
      this.cashSavings +
      this.emergencyFund +
      this.gicTermDeposits +
      this.rrspBalance +
      this.spousalRrspBalance +
      this.tfsaBalance +
      this.fhsaBalance +
      this.respBalance +
      this.liraBalance +
      this.rrifBalance +
      this.dpspBalance +
      this.nonRegisteredInvestments +
      this.dbPensionCommutedValue +
      this.dcPensionBalance +
      this.groupRrspBalance +
      this.stockOptionsRsusValue
    );
  }

  toJSON(): InvestableAssetsSettingsData {
    return {
      cashSavings: this.cashSavings,
      emergencyFund: this.emergencyFund,
      gicTermDeposits: this.gicTermDeposits,
      rrspBalance: this.rrspBalance,
      spousalRrspBalance: this.spousalRrspBalance,
      tfsaBalance: this.tfsaBalance,
      fhsaBalance: this.fhsaBalance,
      respBalance: this.respBalance,
      liraBalance: this.liraBalance,
      rrifBalance: this.rrifBalance,
      dpspBalance: this.dpspBalance,
      nonRegisteredInvestments: this.nonRegisteredInvestments,
      dbPensionCommutedValue: this.dbPensionCommutedValue,
      dcPensionBalance: this.dcPensionBalance,
      groupRrspBalance: this.groupRrspBalance,
      stockOptionsRsusValue: this.stockOptionsRsusValue,
    };
  }
}

/**
 * Real Estate & Other Assets Settings with age-average defaults
 *
 * Covers real property, business interests, insurance cash value,
 * and depreciating physical assets (vehicles, collectibles, etc.).
 */
export class RealEstateAssetsSettings extends SettingsBase<RealEstateAssetsSettingsData> {
  get primaryResidence(): number {
    return this.getNumericFieldValue("primaryResidence");
  }
  get vacationProperty(): number {
    return this.getNumericFieldValue("vacationProperty");
  }
  get investmentProperties(): number {
    return this.getNumericFieldValue("investmentProperties");
  }
  get landOtherRealEstate(): number {
    return this.getNumericFieldValue("landOtherRealEstate");
  }
  get businessValue(): number {
    return this.getNumericFieldValue("businessValue");
  }
  get shareholderLoansReceivable(): number {
    return this.getNumericFieldValue("shareholderLoansReceivable");
  }
  get insuranceCashValue(): number {
    return this.getNumericFieldValue("insuranceCashValue");
  }
  get otherAssets(): number {
    return this.getNumericFieldValue("otherAssets");
  }
  get depreciatingAssets(): number {
    return this.getNumericFieldValue("depreciatingAssets");
  }

  get totalRealEstateAssets(): number {
    return (
      this.primaryResidence +
      this.vacationProperty +
      this.investmentProperties +
      this.landOtherRealEstate +
      this.businessValue +
      this.shareholderLoansReceivable +
      this.insuranceCashValue +
      this.otherAssets +
      this.depreciatingAssets
    );
  }

  toJSON(): RealEstateAssetsSettingsData {
    return {
      primaryResidence: this.primaryResidence,
      vacationProperty: this.vacationProperty,
      investmentProperties: this.investmentProperties,
      landOtherRealEstate: this.landOtherRealEstate,
      businessValue: this.businessValue,
      shareholderLoansReceivable: this.shareholderLoansReceivable,
      insuranceCashValue: this.insuranceCashValue,
      otherAssets: this.otherAssets,
      depreciatingAssets: this.depreciatingAssets,
    };
  }
}

/**
 * Liabilities Settings with age-average defaults
 * Note: Monthly payments are tracked in ExpensesSettingsWithDefaults, not here.
 * This only contains liability balances and interest rates.
 */
export class LiabilitiesSettings extends SettingsBase<LiabilitiesSettingsData> {
  get mortgage(): number {
    return this.getNumericFieldValue("mortgage");
  }

  get mortgageInterestRate(): number {
    return this.getNumericFieldValue("mortgageInterestRate");
  }

  get creditCardBalance(): number {
    return this.getNumericFieldValue("creditCardBalance");
  }

  get creditCardInterestRate(): number {
    return this.getNumericFieldValue("creditCardInterestRate");
  }

  get carLoan(): number {
    return this.getNumericFieldValue("carLoan");
  }

  get carLoanInterestRate(): number {
    return this.getNumericFieldValue("carLoanInterestRate");
  }

  get lineOfCredit(): number {
    return this.getNumericFieldValue("lineOfCredit");
  }

  get lineOfCreditInterestRate(): number {
    return this.getNumericFieldValue("lineOfCreditInterestRate");
  }

  get studentLoans(): number {
    return this.getNumericFieldValue("studentLoans");
  }

  get studentLoanInterestRate(): number {
    return this.getNumericFieldValue("studentLoanInterestRate");
  }

  get otherLiabilities(): number {
    return this.getNumericFieldValue("otherLiabilities");
  }

  get otherLiabilitiesInterestRate(): number {
    return this.getNumericFieldValue("otherLiabilitiesInterestRate");
  }

  get investmentPropertyMortgage(): number {
    return this.getNumericFieldValue("investmentPropertyMortgage");
  }

  get heloc(): number {
    return this.getNumericFieldValue("heloc");
  }

  get businessLoans(): number {
    return this.getNumericFieldValue("businessLoans");
  }

  get taxesPayable(): number {
    return this.getNumericFieldValue("taxesPayable");
  }

  /**
   * Calculate total liability balances (sum of all debt balances,
   * including expanded fields: investment property mortgage, HELOC,
   * business loans, and taxes payable)
   */
  get totalLiabilities(): number {
    return (
      this.mortgage +
      this.creditCardBalance +
      this.carLoan +
      this.lineOfCredit +
      this.studentLoans +
      this.otherLiabilities +
      this.investmentPropertyMortgage +
      this.heloc +
      this.businessLoans +
      this.taxesPayable
    );
  }

  /**
   * Get raw data for serialization
   */
  toJSON(): LiabilitiesSettingsData {
    return {
      mortgage: this.mortgage,
      creditCardBalance: this.creditCardBalance,
      carLoan: this.carLoan,
      lineOfCredit: this.lineOfCredit,
      studentLoans: this.studentLoans,
      otherLiabilities: this.otherLiabilities,
      mortgageInterestRate: this.mortgageInterestRate,
      creditCardInterestRate: this.creditCardInterestRate,
      carLoanInterestRate: this.carLoanInterestRate,
      lineOfCreditInterestRate: this.lineOfCreditInterestRate,
      studentLoanInterestRate: this.studentLoanInterestRate,
      otherLiabilitiesInterestRate: this.otherLiabilitiesInterestRate,
      investmentPropertyMortgage: this.investmentPropertyMortgage,
      heloc: this.heloc,
      businessLoans: this.businessLoans,
      taxesPayable: this.taxesPayable,
    };
  }
}

/**
 * Income Settings with age-average defaults
 */
export class IncomeSettings extends SettingsBase<IncomeSettingsData> {
  get employmentIncome(): number {
    return this.getNumericFieldValue("employmentIncome");
  }

  get pensionIncome(): number {
    return this.getNumericFieldValue("pensionIncome");
  }

  get cppIncome(): number {
    return this.getNumericFieldValue("cppIncome");
  }

  get oasIncome(): number {
    return this.getNumericFieldValue("oasIncome");
  }

  get investmentIncome(): number {
    return this.getNumericFieldValue("investmentIncome");
  }

  get rentalIncome(): number {
    return this.getNumericFieldValue("rentalIncome");
  }

  get otherIncome(): number {
    return this.getNumericFieldValue("otherIncome");
  }

  /**
   * Calculate total monthly income (sum of all income sources)
   */
  get monthlyIncome(): number {
    return (
      this.employmentIncome +
      this.pensionIncome +
      this.cppIncome +
      this.oasIncome +
      this.investmentIncome +
      this.rentalIncome +
      this.otherIncome
    );
  }

  /**
   * Get raw data for serialization
   */
  toJSON(): IncomeSettingsData {
    return {
      employmentIncome: this.employmentIncome,
      pensionIncome: this.pensionIncome,
      cppIncome: this.cppIncome,
      oasIncome: this.oasIncome,
      investmentIncome: this.investmentIncome,
      rentalIncome: this.rentalIncome,
      otherIncome: this.otherIncome,
    };
  }
}

/**
 * Expenses Settings with age-average defaults
 */
export class ExpensesSettings extends SettingsBase<ExpensesSettingsData> {
  get housing(): number {
    return this.getNumericFieldValue("housing");
  }

  get utilities(): number {
    return this.getNumericFieldValue("utilities");
  }

  get food(): number {
    return this.getNumericFieldValue("food");
  }

  get transportation(): number {
    return this.getNumericFieldValue("transportation");
  }

  get insurance(): number {
    return this.getNumericFieldValue("insurance");
  }

  get healthcare(): number {
    return this.getNumericFieldValue("healthcare");
  }

  get other(): number {
    return this.getNumericFieldValue("other");
  }

  get mortgagePayment(): number {
    return this.getNumericFieldValue("mortgagePayment");
  }

  get creditCardPayment(): number {
    return this.getNumericFieldValue("creditCardPayment");
  }

  get carLoanPayment(): number {
    return this.getNumericFieldValue("carLoanPayment");
  }

  get lineOfCreditPayment(): number {
    return this.getNumericFieldValue("lineOfCreditPayment");
  }

  get studentLoanPayment(): number {
    return this.getNumericFieldValue("studentLoanPayment");
  }

  get otherLiabilitiesPayment(): number {
    return this.getNumericFieldValue("otherLiabilitiesPayment");
  }

  /**
   * Calculate total monthly expenses (sum of all expense categories including debt payments)
   */
  get monthlyExpenses(): number {
    return (
      this.housing +
      this.utilities +
      this.food +
      this.transportation +
      this.insurance +
      this.healthcare +
      this.other +
      this.mortgagePayment +
      this.creditCardPayment +
      this.carLoanPayment +
      this.lineOfCreditPayment +
      this.studentLoanPayment +
      this.otherLiabilitiesPayment
    );
  }

  /**
   * Calculate total debt payments (sum of all debt payment fields)
   */
  get totalDebtPayments(): number {
    return (
      this.mortgagePayment +
      this.creditCardPayment +
      this.carLoanPayment +
      this.lineOfCreditPayment +
      this.studentLoanPayment +
      this.otherLiabilitiesPayment
    );
  }

  /**
   * Get raw data for serialization
   */
  toJSON(): ExpensesSettingsData {
    return {
      housing: this.housing,
      utilities: this.utilities,
      food: this.food,
      transportation: this.transportation,
      insurance: this.insurance,
      healthcare: this.healthcare,
      other: this.other,
      mortgagePayment: this.mortgagePayment,
      creditCardPayment: this.creditCardPayment,
      carLoanPayment: this.carLoanPayment,
      lineOfCreditPayment: this.lineOfCreditPayment,
      studentLoanPayment: this.studentLoanPayment,
      otherLiabilitiesPayment: this.otherLiabilitiesPayment,
    };
  }
}

/**
 * Assumptions Settings with age-average defaults
 *
 * Assumption fields are NOT under the user's direct financial control,
 * so unset values fall back to age-appropriate statistics (not zero).
 */
export class AssumptionsSettings extends SettingsBase<AssumptionsSettingsData> {
  /**
   * Override: unset assumption fields fall back to age-appropriate statistics.
   */
  protected override getNumericFieldValue(
    fieldName: keyof AssumptionsSettingsData & string
  ): number {
    if (this.data[fieldName] !== null && this.data[fieldName] !== undefined) {
      const value = this.data[fieldName];
      return typeof value === "number" ? value : 0;
    }

    return getFieldStatisticValue(this.statisticsMap, fieldName);
  }

  get retirementAge(): number {
    return this.getNumericFieldValue("retirementAge");
  }

  get lifeExpectancy(): number {
    return this.getNumericFieldValue("lifeExpectancy");
  }

  get expectedReturnRate(): number {
    return this.getNumericFieldValue("expectedReturnRate");
  }

  get inflationRate(): number {
    return this.getNumericFieldValue("inflationRate");
  }

  get expensesAtRetirement(): number {
    return this.getNumericFieldValue("expensesAtRetirement");
  }

  get realEstateAppreciationRate(): number {
    return this.getNumericFieldValue("realEstateAppreciationRate");
  }

  get otherAssetGrowthRate(): number {
    return this.getNumericFieldValue("otherAssetGrowthRate");
  }

  get depreciationRate(): number {
    return this.getNumericFieldValue("depreciationRate");
  }

  /**
   * Get raw data for serialization
   */
  toJSON(): AssumptionsSettingsData {
    return {
      retirementAge: this.retirementAge,
      lifeExpectancy: this.lifeExpectancy,
      expectedReturnRate: this.expectedReturnRate,
      inflationRate: this.inflationRate,
      expensesAtRetirement: this.expensesAtRetirement,
      realEstateAppreciationRate: this.realEstateAppreciationRate,
      otherAssetGrowthRate: this.otherAssetGrowthRate,
      depreciationRate: this.depreciationRate,
    };
  }
}

/**
 * Combined settings object with all categories
 */
export interface Settings {
  profile: ProfileSettings;
  investableAssets: InvestableAssetsSettings;
  realEstateAssets: RealEstateAssetsSettings;
  liabilities: LiabilitiesSettings;
  income: IncomeSettings;
  expenses: ExpensesSettings;
  assumptions: AssumptionsSettings;
}

interface SettingsContextType {
  settings: Settings;
  loading: boolean;
  error: string | null;
  statisticsLoading: boolean;
  statisticsInitialized: boolean;
  statisticsMap: AgeStatistics;
}

const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
);

/**
 * Internal state for settings data fetched from Firestore
 */
interface SettingsData {
  profile: ProfileSettingsData;
  investableAssets: InvestableAssetsSettingsData;
  realEstateAssets: RealEstateAssetsSettingsData;
  liabilities: LiabilitiesSettingsData;
  income: IncomeSettingsData;
  expenses: ExpensesSettingsData;
  assumptions: AssumptionsSettingsData;
}

interface SettingsProviderProps {
  children: React.ReactNode;
}

/**
 * Settings Provider
 *
 * Provides settings context with automatic age-based defaults.
 * Loads statistics per-age via loadStatisticsForAge(), then subscribes to each
 * settings category document individually via onSnapshot.
 */

const emptySettings: SettingsData = {
  profile: {} as ProfileSettingsData,
  investableAssets: {} as InvestableAssetsSettingsData,
  realEstateAssets: {} as RealEstateAssetsSettingsData,
  liabilities: {} as LiabilitiesSettingsData,
  income: {} as IncomeSettingsData,
  expenses: {} as ExpensesSettingsData,
  assumptions: {} as AssumptionsSettingsData,
};

const SETTINGS_CATEGORIES = [
  "profile",
  "investableAssets",
  "realEstateAssets",
  "liabilities",
  "income",
  "expenses",
  "assumptions",
] as const;

/** Loads age-based statistics JSON; re-fetches when age changes. */
function useStatisticsLoader(age: number | null) {
  const [statisticsMap, setStatisticsMap] = useState<AgeStatistics>({});
  const [statisticsLoading, setStatisticsLoading] = useState(true);
  const [statisticsInitialized, setStatisticsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const effectiveAge = age ?? DEFAULT_AGE;

    loadStatisticsForAge(effectiveAge)
      .then((stats: AgeStatistics) => {
        if (!cancelled) {
          setStatisticsMap(stats);
          setStatisticsInitialized(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          logger.error("Failed to load statistics", { error: err });
          setError(
            `Failed to load statistics: ${err instanceof Error ? err.message : "Unknown error"}`
          );
          setStatisticsInitialized(true);
        }
      })
      .finally(() => {
        if (!cancelled) setStatisticsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [age]);

  return { statisticsMap, statisticsLoading, statisticsInitialized, error };
}

/** Subscribes to all 7 settings category documents for the given user. */
function useFirestoreSettingsSubscription(userId: string | undefined) {
  const [settingsData, setSettingsData] = useState<SettingsData>(emptySettings);
  const [categoryReady, setCategoryReady] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (!userId) return;

    const unsubscribers = SETTINGS_CATEGORIES.map((category) =>
      subscribeToSettings(userId, category, (data) => {
        setSettingsData((prev) => ({
          ...prev,
          [category]: data ?? emptySettings[category],
        }));
        setCategoryReady((prev) => ({ ...prev, [category]: true }));
      })
    );

    return () => {
      unsubscribers.forEach((unsub) => unsub());
      setSettingsData(emptySettings);
      setCategoryReady({});
    };
  }, [userId]);

  return { settingsData, categoryReady };
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const { settingsData, categoryReady } = useFirestoreSettingsSubscription(
    user?.userId
  );

  // Derive age from profile settings data (no separate context needed)
  const profileYearOfBirth = settingsData.profile.yearOfBirth;
  const age =
    profileYearOfBirth != null
      ? calculateAgeFromYear(profileYearOfBirth)
      : null;

  const { statisticsMap, statisticsLoading, statisticsInitialized, error } =
    useStatisticsLoader(age);

  // Derive loading from statistics + per-category listener readiness
  const loading =
    statisticsLoading ||
    (!!user?.userId && Object.keys(categoryReady).length < 7);

  const effectiveAge = age ?? DEFAULT_AGE;

  const settings = useMemo<Settings>(() => {
    return {
      profile: new ProfileSettings(
        settingsData.profile,
        effectiveAge,
        statisticsMap
      ),
      investableAssets: new InvestableAssetsSettings(
        settingsData.investableAssets,
        effectiveAge,
        statisticsMap
      ),
      realEstateAssets: new RealEstateAssetsSettings(
        settingsData.realEstateAssets,
        effectiveAge,
        statisticsMap
      ),
      liabilities: new LiabilitiesSettings(
        settingsData.liabilities,
        effectiveAge,
        statisticsMap
      ),
      income: new IncomeSettings(
        settingsData.income,
        effectiveAge,
        statisticsMap
      ),
      expenses: new ExpensesSettings(
        settingsData.expenses,
        effectiveAge,
        statisticsMap
      ),
      assumptions: new AssumptionsSettings(
        settingsData.assumptions,
        effectiveAge,
        statisticsMap
      ),
    };
  }, [settingsData, effectiveAge, statisticsMap]);

  const value: SettingsContextType = {
    settings,
    loading,
    error,
    statisticsLoading,
    statisticsInitialized,
    statisticsMap,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return context;
}
